import Cookies from 'js-cookie';
import {
  APP_URL_QUERY_PARAMS,
  BUSINESS_UNITS,
  DEFAULT_IFRAME_HEIGHT,
  ENVS,
  FALABELLA_COOKIES,
  LANGUAGES,
  PORTUGUESE_COUNTRIES,
  POST_MESSAGE_TYPES,
  PRICE_TYPES,
  PRICE_TYPES_FA,
} from './constants';
import {
  SHORT_BUSINESS_UNITS,
  Environments,
  IMAGE_HOSTS,
  defaultContentsToInclude,
  CONTENT_STACK_HOSTS,
  THUMBNAIL_PLACEHOLDER,
  DESKTOP_MAIN_IMAGE_PLACEHOLDER,
  MOBILE_MAIN_IMAGE_PLACEHOLDER,
  TENANT_CROSS_CONFIG,
  TENANT_NAMES,
} from './constants/urls';
import { isSodimacRetail } from '@digital-retail/store-manager';
import { Tenants } from './common.model';
import { get } from 'lodash';
import { getTopLevelDomain } from './location';

export const getTenantCrossConfigKey = (
  tenant: string,
  store?: string
): string => {
  if (store && isSodimacRetail(store)) {
    const sodimacRetailKey =
      TENANT_NAMES.SODIMAC_RETAIL[
        tenant as keyof typeof TENANT_NAMES.SODIMAC_RETAIL
      ];
    return sodimacRetailKey || tenant;
  }
  return tenant;
};

export const resizeObserverHandler = (entries: any) => {
  for (const entry of entries) {
    const pageHeight = entry.target?.offsetHeight || null;
    let finalHeight;

    if (!pageHeight || pageHeight < 120) {
      finalHeight = DEFAULT_IFRAME_HEIGHT;
    } else {
      finalHeight = pageHeight;
    }

    window.parent.postMessage(
      {
        iframeHeight: `${finalHeight}px`,
        type: POST_MESSAGE_TYPES.SET_IFRAME_HEIGHT,
      },
      '*'
    );
  }
};

export const updateImageUrlHost = (url: string) => {
  if (!url) return '';
  const urlObject = new URL(url);
  const tenant = getCurrentTenant();
  const country = (tenant.substring(0, 2).toUpperCase() as 'SO' | 'FA') || 'SO';
  const currentHost = urlObject.host;

  if (CONTENT_STACK_HOSTS.includes(currentHost)) {
    urlObject.host = IMAGE_HOSTS[country];
  }

  return urlObject.toString();
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
// istanbul ignore next
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = () => {};

export const getMobileAppStatus = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const isMobileApp =
    queryParams.get(APP_URL_QUERY_PARAMS.IS_MOBILE_APP) === 'true';

  return isMobileApp;
};

export const isSodimacBusinessUnit = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const tenant = queryParams.get(APP_URL_QUERY_PARAMS.TENANT) || '';
  const country = (tenant.substring(0, 2).toUpperCase() as 'SO' | 'FA') || 'SO';

  return country === SHORT_BUSINESS_UNITS.SO;
};

export const getCurrentAppEnvironment = () => {
  let env = process.env.REACT_APP_ENVIRONMENT as Environments;
  if (ENVS.includes(env) === false) {
    env = 'production';
  }

  return env;
};

export const getCurrentTenant = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const tenant = queryParams.get(APP_URL_QUERY_PARAMS.TENANT) || '';

  return tenant.toUpperCase() as Tenants;
};

export const getCurrentStore = (): string => {
  const queryParams = new URLSearchParams(window.location.search);
  const storeFromQuery =
    queryParams.get(APP_URL_QUERY_PARAMS.STORE) || BUSINESS_UNITS.SODIMAC;

  return storeFromQuery;
};

export const getZones = () => {
  const isMobileApp = getMobileAppStatus();
  let parsedZones = '';
  if (isMobileApp) {
    const queryParams = new URLSearchParams(window.location.search);
    parsedZones = queryParams.get(APP_URL_QUERY_PARAMS.ZONES) || '';
    return parsedZones;
  }
  const tenant = getCurrentTenant();
  const store = getCurrentStore();
  const configKey = getTenantCrossConfigKey(tenant, store);
  const zoneCookieName = get(
    TENANT_CROSS_CONFIG,
    `${configKey}.zoneCookieName`,
    ''
  ) as string;
  TENANT_CROSS_CONFIG[configKey] || {};
  parsedZones = Cookies.get(zoneCookieName) || '';
  if (zoneCookieName === FALABELLA_COOKIES.ZONES) {
    parsedZones = parsedZones.slice(1, -1).split('"').join('');
  }
  return parsedZones;
};

export const getPriceGroupId = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const isMobileApp = getMobileAppStatus();
  let pgId = '';
  if (isMobileApp) {
    pgId = queryParams.get(APP_URL_QUERY_PARAMS.PG_ID) || '';
    return pgId;
  }
  const tenant = getCurrentTenant();
  const store = getCurrentStore();
  const configKey = getTenantCrossConfigKey(tenant, store);
  const priceGroupCookieName = get(
    TENANT_CROSS_CONFIG,
    `${configKey}.priceGroupCookieName`,
    ''
  ) as string;
  pgId = Cookies.get(priceGroupCookieName) || '';
  return pgId;
};

export const getPoliticalId = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const isMobileApp = getMobileAppStatus();
  let pId = '';
  if (isMobileApp) {
    pId = queryParams.get(APP_URL_QUERY_PARAMS.PID) || '';
  } else {
    pId = Cookies.get(FALABELLA_COOKIES.PID) || '';
  }

  return pId;
};

export const getPidForSelection = (): string => {
  const queryParams = new URLSearchParams(window.location.search);
  const isMobileApp = getMobileAppStatus();

  if (isMobileApp) {
    const pidFromQuery = queryParams.get(APP_URL_QUERY_PARAMS.PID) || '';
    if (pidFromQuery) {
      return pidFromQuery;
    }
  }

  const pidFromQuery = queryParams.get(APP_URL_QUERY_PARAMS.PID) || '';
  if (pidFromQuery) {
    return pidFromQuery;
  }

  const pidFromCookie = Cookies.get(FALABELLA_COOKIES.PID) || '';
  return pidFromCookie;
};

export const getProductDetailsApiQueryParams = (isSodimacRe?: boolean) => {
  const zones = getZones();
  const pgId = getPriceGroupId();
  let queryparams: {
    zones: string;
    pgId: string;
    pId?: string;
    store?: string;
    exp?: string;
  } = {
    zones,
    pgId,
  };
  const isSodimacBU = isSodimacBusinessUnit();
  if (!isSodimacBU || isSodimacRe) {
    const pId = getPoliticalId();
    queryparams = {
      ...queryparams,
      pId,
    };
  }

  return queryparams;
};

export const isAddToCartAtgEnabled = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const isAtgEnabled =
    queryParams.get(APP_URL_QUERY_PARAMS.IS_ATG_ENABLED) === 'true';

  return isAtgEnabled;
};

export const handleButtonsClick = (
  event: React.KeyboardEvent<HTMLDivElement>,
  callback: () => void
) => {
  if (event.key === 'Enter') {
    callback();
  }
};

export const getCurrentBusinessUnit = () => {
  const isSodimacBU = isSodimacBusinessUnit();
  if (isSodimacBU) {
    return BUSINESS_UNITS.SODIMAC;
  } else {
    return BUSINESS_UNITS.FALABELLA;
  }
};

export const getContentsToInclude = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const contentsToInclude =
    queryParams.get(APP_URL_QUERY_PARAMS.CONTENTS_TO_INCLUDE) ||
    defaultContentsToInclude.join(',');

  return contentsToInclude;
};

export const getCsEntryId = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const csEntryId = queryParams.get(APP_URL_QUERY_PARAMS.CS_ENTRY_ID) || '';

  return csEntryId;
};

export const getCartId = () => {
  const tenant = getCurrentTenant();
  const store = getCurrentStore();
  const configKey = getTenantCrossConfigKey(tenant, store);
  const tenantConfig = get(TENANT_CROSS_CONFIG, configKey);
  const cartIdCookieName = get(tenantConfig, 'cartIdCookieName', '') as string;
  const cartId = Cookies.get(cartIdCookieName) || '';
  return cartId;
};

export const getAuthSid = () => {
  const tenant = getCurrentTenant();
  const store = getCurrentStore();
  const configKey = getTenantCrossConfigKey(tenant, store);
  const tenantConfig = get(TENANT_CROSS_CONFIG, configKey);
  const authSidCookieName = get(
    tenantConfig,
    'authSidCookieName',
    ''
  ) as string;
  const authSid = Cookies.get(authSidCookieName) || '';

  return authSid;
};

export const isNormalPrice = (priceType: string) => {
  const isSodimacBU = isSodimacBusinessUnit();
  const isSodimacRe = isSodimacRetail(getCurrentStore());
  if (isSodimacBU && !isSodimacRe) {
    return priceType === PRICE_TYPES.NORMAL;
  } else {
    return priceType === PRICE_TYPES_FA.NORMAL;
  }
};

export const getPlaceHolderThumbNail = () => THUMBNAIL_PLACEHOLDER;

export const getPlaceholderDesktopMainImage = () =>
  DESKTOP_MAIN_IMAGE_PLACEHOLDER;

export const getPlaceholderMobileMainImage = () =>
  MOBILE_MAIN_IMAGE_PLACEHOLDER;

export const removeSpaces = (str: string) => {
  return str.replace(/\s+/g, '');
};

export const removeSpacesAndLowerCase = (str: string) => {
  if (!str) return '';
  return removeSpaces(str).toLowerCase();
};

export const updateImageURL = ({
  url,
  format,
  width,
}: {
  url: string;
  format: string;
  width: number;
}) => {
  const urlObject = new URL(url);
  urlObject.searchParams.set('format', format);
  urlObject.searchParams.set('width', width.toString());

  return urlObject.toString();
};

export const getLanguage = () => {
  const tenant = getCurrentTenant();
  const { SPANISH, PORTUGUESE } = LANGUAGES;
  if (PORTUGUESE_COUNTRIES.includes(tenant.toLowerCase())) {
    return PORTUGUESE;
  }
  return SPANISH;
};

export const setCartIdCookie = (cartId: string): void => {
  const tenant = getCurrentTenant();
  const store = getCurrentStore();
  const configKey = getTenantCrossConfigKey(tenant, store);
  const tenantConfig = get(TENANT_CROSS_CONFIG, configKey);
  const cartIdCookieName = get(tenantConfig, 'cartIdCookieName', '') as string;
  const cartIdCookie = Cookies.get(cartIdCookieName) || '';
  const isCardIdCookieExists = cartIdCookie !== '';
  if (isCardIdCookieExists) {
    return;
  }
  const topLeveldomain = getTopLevelDomain();
  Cookies.set(cartIdCookieName, cartId, {
    expires: 365,
    domain: `.${topLeveldomain}`,
    Priority: 'High',
  });
};
